import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Decanting the Supernatant',
    content: 'Decanting is the process of carefully pouring off the clear liquid (supernatant) while leaving the solid precipitate undisturbed. The supernatant contains ZnSO4 and excess H₂SO₄ that must be removed.',
    whatHappens: 'The clear, colorless supernatant is carefully poured off. The copper precipitate remains at the bottom of the container.',
    observe: [
      'Supernatant should be colorless (if blue, reaction incomplete)',
      'Pour slowly to avoid disturbing precipitate',
      'Copper remains at bottom as a reddish-brown mass',
      'Some liquid may remain - this is acceptable'
    ]
  },
  2: {
    title: 'Water Wash - Removing Ionic Residue',
    content: 'Deionized water washes remove soluble ionic compounds (ZnSO4, residual acid) from the copper surface. Multiple washes (2x 5 mL) ensure thorough removal of contaminants that would affect the final mass.',
    whatHappens: 'Water is added, the mixture is swirled gently, and the wash water is decanted. This process is repeated to remove ionic impurities.',
    observe: [
      'Add about 5 mL deionized water',
      'Swirl gently to contact all copper surfaces',
      'Decant wash water carefully',
      'Repeat for second wash'
    ]
  },
  3: {
    title: 'Methanol Wash - Displacing Water',
    content: 'Methanol (CH3OH) is miscible with water and displaces it from the copper surface. Methanol has lower surface tension than water, allowing better penetration into the copper mass.',
    whatHappens: 'Methanol wash removes most of the water from the copper. Methanol is more volatile than water, facilitating faster drying.',
    observe: [
      'Add about 5 mL methanol',
      'Swirl briefly to mix',
      'Decant methanol wash',
      'Copper may appear slightly shinier'
    ]
  },
  4: {
    title: 'Acetone Wash - Quick Drying',
    content: 'Acetone ((CH3)2CO) is extremely volatile and miscible with methanol. It rapidly evaporates at room temperature, carrying away any remaining moisture. This allows drying without heating.',
    whatHappens: 'Acetone wash displaces the methanol and evaporates quickly. The copper can now air dry without prolonged oven heating.',
    observe: [
      'Add about 5 mL acetone',
      'Acetone evaporates noticeably fast',
      'Copper surface becomes drier',
      'Ready for final drying step'
    ]
  }
};

export default function Part7Washing() {
  const [decanted, setDecanted] = useState(false);
  const [waterWashDone, setWaterWashDone] = useState(false);
  const [methanolWashDone, setMethanolWashDone] = useState(false);
  const [acetoneWashDone, setAcetoneWashDone] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [decanted, waterWashDone, methanolWashDone, acetoneWashDone].filter(Boolean).length;

  const handleDecant = useCallback(() => {
    if (!decanted) {
      setDecanted(true);
      setCurrentStep(2);
    }
  }, [decanted]);

  const handleWaterWash = useCallback(() => {
    if (decanted && !waterWashDone) {
      setWaterWashDone(true);
      setCurrentStep(3);
    }
  }, [decanted, waterWashDone]);

  const handleMethanolWash = useCallback(() => {
    if (waterWashDone && !methanolWashDone) {
      setMethanolWashDone(true);
      setCurrentStep(4);
    }
  }, [waterWashDone, methanolWashDone]);

  const handleAcetoneWash = useCallback(() => {
    if (methanolWashDone && !acetoneWashDone) {
      setAcetoneWashDone(true);
    }
  }, [methanolWashDone, acetoneWashDone]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IX: Wash and Dry</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Decant Supernatant', hint: 'Pour off ZnSO4 solution', done: decanted },
            { id: 2, title: 'Water Wash (2x)', hint: 'Remove ionic residue', done: waterWashDone },
            { id: 3, title: 'Methanol Wash', hint: 'Displace water', done: methanolWashDone },
            { id: 4, title: 'Acetone Wash', hint: 'Quick drying', done: acetoneWashDone }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Wash counter */}
        <div className="wash-counter">
          <span>Washes Complete:</span>
          <div className="wash-dots">
            <div className={`dot ${decanted ? 'done' : ''}`} title="Decant" />
            <div className={`dot ${waterWashDone ? 'done' : ''}`} title="H₂O" />
            <div className={`dot ${methanolWashDone ? 'done' : ''}`} title="MeOH" />
            <div className={`dot ${acetoneWashDone ? 'done' : ''}`} title="Acetone" />
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Copper Washing and Drying"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['Evaporating dish', 'Wash bottles', 'Transfer pipette']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Wash bottles */}
            <div className="wash-bottles">
              <div
                className={`bottle ${currentStep === 2 && !waterWashDone ? 'active' : ''}`}
                onClick={handleWaterWash}
                style={{ cursor: currentStep === 2 && !waterWashDone ? 'pointer' : 'default' }}
              >
                <svg viewBox="0 0 40 70" width={35} height={60}>
                  <path d="M15 20 L15 55 Q15 60 20 60 L20 60 Q25 60 25 55 L25 20" fill="#E6F3FF" stroke="#888" strokeWidth="1" />
                  <path d="M17 10 L23 10 L25 20 L15 20 Z" fill="#3B82F6" />
                  <text x="20" y="68" fontSize="6" fill="#E0E0E0" textAnchor="middle">H₂O</text>
                </svg>
              </div>
              <div
                className={`bottle ${currentStep === 3 && !methanolWashDone ? 'active' : ''}`}
                onClick={handleMethanolWash}
                style={{ cursor: currentStep === 3 && !methanolWashDone ? 'pointer' : 'default' }}
              >
                <svg viewBox="0 0 40 70" width={35} height={60}>
                  <path d="M15 20 L15 55 Q15 60 20 60 L20 60 Q25 60 25 55 L25 20" fill="#FFFAF0" stroke="#888" strokeWidth="1" />
                  <path d="M17 10 L23 10 L25 20 L15 20 Z" fill="#FF6600" />
                  <text x="20" y="68" fontSize="5" fill="#E0E0E0" textAnchor="middle">MeOH</text>
                </svg>
              </div>
              <div
                className={`bottle ${currentStep === 4 && !acetoneWashDone ? 'active' : ''}`}
                onClick={handleAcetoneWash}
                style={{ cursor: currentStep === 4 && !acetoneWashDone ? 'pointer' : 'default' }}
              >
                <svg viewBox="0 0 40 70" width={35} height={60}>
                  <path d="M15 20 L15 55 Q15 60 20 60 L20 60 Q25 60 25 55 L25 20" fill="#FFFFFF" stroke="#888" strokeWidth="1" />
                  <path d="M17 10 L23 10 L25 20 L15 20 Z" fill="#9333EA" />
                  <text x="20" y="68" fontSize="5" fill="#E0E0E0" textAnchor="middle">Acetone</text>
                </svg>
              </div>
            </div>

            {/* Evaporating dish with copper */}
            <div
              className={`equipment-station ${currentStep === 1 && !decanted ? 'clickable' : ''}`}
              onClick={handleDecant}
              style={{ cursor: currentStep === 1 && !decanted ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 160 120" width={180} height={140}>
                <defs>
                  <linearGradient id={`dish-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E8E0D5" />
                    <stop offset="50%" stopColor="#F5F0E8" />
                    <stop offset="100%" stopColor="#E8E0D5" />
                  </linearGradient>
                </defs>

                {/* Porcelain dish */}
                <ellipse cx="80" cy="80" rx="65" ry="20" fill={`url(#dish-${id})`} stroke="#C8C0B5" strokeWidth="2" />
                <path d="M15 80 Q15 50 80 50 Q145 50 145 80" fill="none" stroke="#C8C0B5" strokeWidth="2" />

                {/* Supernatant (before decanting) */}
                {!decanted && (
                  <ellipse cx="80" cy="72" rx="55" ry="15" fill="rgba(255,255,255,0.5)" />
                )}

                {/* Copper precipitate */}
                <ellipse
                  cx="80"
                  cy="78"
                  rx={decanted ? 50 : 45}
                  ry="12"
                  fill={copperCycleColors.copperPrecipitate}
                  opacity={acetoneWashDone ? 1 : 0.9}
                />

                {/* Shiny copper when clean */}
                {acetoneWashDone && (
                  <g>
                    <ellipse cx="70" cy="75" rx="15" ry="5" fill="#D4A373" opacity="0.5" />
                    <circle cx="60" cy="73" r="2" fill="#FFF" opacity="0.6">
                      <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </g>
                )}

                {/* Wash droplets animation */}
                {(currentStep === 2 || currentStep === 3 || currentStep === 4) && !acetoneWashDone && (
                  <g>
                    {[...Array(3)].map((_, i) => (
                      <ellipse
                        key={i}
                        cx={60 + i * 20}
                        cy={40}
                        rx={3}
                        ry={4}
                        fill={currentStep === 2 ? '#87CEEB' : currentStep === 3 ? '#FFFAF0' : '#E6E6FA'}
                        opacity="0.7"
                      >
                        <animate
                          attributeName="cy"
                          values="40;75;40"
                          dur={`${1 + i * 0.2}s`}
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.7;0.3;0"
                          dur={`${1 + i * 0.2}s`}
                          repeatCount="indefinite"
                        />
                      </ellipse>
                    ))}
                  </g>
                )}

                {/* Boiling chips */}
                <circle cx="60" cy="76" r="2" fill={copperCycleColors.boilingChips} />
                <circle cx="100" cy="77" r="2" fill={copperCycleColors.boilingChips} />

                <text x="80" y="110" fontSize="10" fill="#E0E0E0" textAnchor="middle">Evaporating Dish</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !decanted && (
              <div className="status-message info">
                <Droplets size={20} />
                Decant the clear supernatant from the copper
              </div>
            )}
            {currentStep === 2 && !waterWashDone && (
              <div className="status-message info">
                <Droplets size={20} />
                Wash with deionized water (2x5 mL) to remove salts
              </div>
            )}
            {currentStep === 3 && !methanolWashDone && (
              <div className="status-message info">
                <Droplets size={20} />
                Wash with methanol (5 mL) to displace water (FLAMMABLE - no open flames!)
              </div>
            )}
            {currentStep === 4 && !acetoneWashDone && (
              <div className="status-message info">
                <Droplets size={20} />
                Wash with acetone (5 mL) for quick drying (VERY FLAMMABLE - be careful!)
              </div>
            )}
            {acetoneWashDone && (
              <div className="status-message success">
                <Check size={20} />
                Copper washed and ready for drying!
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Why Multiple Washes?</h4>
            <p>
              <strong>Water:</strong> Removes ionic impurities (ZnSO4, excess acid)
            </p>
            <p>
              <strong>Methanol:</strong> Miscible with water, helps displace it
            </p>
            <p>
              <strong>Acetone:</strong> Very volatile, evaporates quickly for fast drying without heating
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            justify-content: center;
          }

          .wash-bottles {
            display: flex;
            gap: 10px;
            align-items: flex-end;
          }

          .bottle {
            padding: 5px;
            border-radius: 6px;
            transition: all 0.2s;
            opacity: 0.6;
          }

          .bottle.active {
            opacity: 1;
            cursor: pointer;
            background: rgba(0, 168, 107, 0.1);
          }

          .bottle.active:hover {
            transform: scale(1.05);
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .wash-counter {
            margin-top: 16px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .wash-counter span {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .wash-dots {
            display: flex;
            gap: 8px;
            margin-top: 8px;
          }

          .dot {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #333;
            border: 2px solid #555;
            transition: all 0.3s;
          }

          .dot.done {
            background: var(--secondary);
            border-color: var(--secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p strong {
            color: var(--text-primary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
